import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CustomBreadcrumb, CustomButton, CustomSearchableSelect } from '@/shared/components/atoms';
import { CustomCommonScreenHeader, CustomTable, CustomTableColumn } from '@/shared/components/molecules';
import { Home, Ticket as TicketIcon, Search, Clock, Inbox, Filter, X, RotateCw, MessageSquare } from 'lucide-react';
import { useAuthStore } from '@/shared/stores/authStore';
import { useToastStore } from '@/shared/stores/toastStore';
import { ticketApi } from '../api/ticketApi';
import { departmentApi } from '@/features/school-admin/api/departmentApi';
import { Ticket, TicketStatus } from '../types';
import { Department } from '@/features/school-admin/types';
import { DEPT_COLOR_MAP } from '@/features/dashboard/data/dashboardDemoData';
import '../styles/ticketing.css';

const TICKET_STATUS_OPTIONS: { label: string; value: TicketStatus | 'all' }[] = [
    { label: 'All', value: 'all' },
    { label: 'Open', value: 'open' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'Overdue', value: 'overdue' },
    { label: 'Resolved', value: 'resolved' },
    { label: 'Escalated', value: 'escalated' },
    { label: 'Forwarded', value: 'forwarded' },
    { label: 'Re Opened', value: 'reopened' },
];

const getTodayDateString = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const TicketingApp: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useAuthStore();
    const schoolSlug = user?.school?.slug || '';

    // State
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Filters — initialize department and status from URL query param if present
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<TicketStatus | 'all'>((searchParams.get('status') as TicketStatus) || 'all');
    const [deptFilter, setDeptFilter] = useState<string>(searchParams.get('department_id') || 'all');
    const { showToast } = useToastStore();
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');

    // Pagination
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const fetchTickets = useCallback(async () => {
        if (!schoolSlug) return;
        setLoading(true);
        setError(null);
        try {
            const params: any = {
                page,
                page_size: pageSize,
            };

            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }

            if (deptFilter !== 'all') {
                params.department_id = deptFilter;
            }

            if (fromDate) {
                params.from_date = fromDate;
            }

            if (toDate) {
                params.to_date = toDate;
            }

            const response = await ticketApi.getAdminTickets(schoolSlug, params);
            if (response.success && response.data) {
                const data = response.data as any;
                if (Array.isArray(data)) {
                    setTickets(data);
                    setTotalCount(data.length);
                    setTotalPages(1);
                } else {
                    setTickets(data.items || []);
                    setTotalCount(data.total_count || 0);
                    setTotalPages(data.total_pages || 1);
                }
            } else {
                setError(response.error || 'Failed to fetch tickets');
                setTickets([]);
            }
        } catch (err: any) {
            setError(err.message || 'An error occurred while fetching tickets');
            setTickets([]);
        } finally {
            setLoading(false);
        }
    }, [schoolSlug, statusFilter, deptFilter, fromDate, toDate, page, pageSize]);

    const fetchDepartments = useCallback(async () => {
        if (!schoolSlug) return;
        try {
            const response = await departmentApi.getDepartments(schoolSlug, { page: 1, page_size: 50 });
            if (response.success && response.data) {
                const data = response.data as any;
                setDepartments(Array.isArray(data) ? data : (data.items || []));
            }
        } catch (err: any) {
            console.error('Failed to fetch departments:', err);
        }
    }, [schoolSlug]);

    useEffect(() => {
        const statusParam = searchParams.get('status') as TicketStatus | null;
        if (statusParam) {
            setStatusFilter(statusParam);
        }
        const deptParam = searchParams.get('department_id');
        if (deptParam) {
            setDeptFilter(deptParam);
        }
    }, [searchParams]);

    useEffect(() => {
        fetchDepartments();
    }, [fetchDepartments]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    // Local client-side search filtering
    const filteredTickets = tickets.filter(ticket => {
        if (!search) return true;
        const s = search.toLowerCase();
        const subject = (ticket.subject || ticket.name || '').toLowerCase();
        const description = (ticket.description || '').toLowerCase();
        const id = (ticket.id || '').toLowerCase();
        const studentName = (ticket.students?.name || ticket.creator_details?.name || '').toLowerCase();
        return subject.includes(s) || description.includes(s) || id.includes(s) || studentName.includes(s);
    });

    const handleFromDateChange = (val: string) => {
        setFromDate(val);
        setPage(1);
    };

    const handleToDateChange = (val: string) => {
        setToDate(val);
        setPage(1);
    };

    const handleResetFilters = () => {
        setSearch('');
        setStatusFilter('all');
        setDeptFilter('all');
        setFromDate('');
        setToDate('');
        setPage(1);
    };

    const getStatusColor = (status: TicketStatus) => {
        switch (status) {
            case 'open': return '#16a34a';
            case 'in_progress': return '#3b82f6';
            case 'forwarded': return '#8b5cf6';
            case 'resolved': return '#0891b2';
            case 'escalated': return '#ef4444';
            case 'overdue': return '#dc2626';
            case 'reopened': return '#f59e0b';
            case 'rejected': return '#ef4444';
            case 'closed': return '#6b7280';
            default: return '#16a34a';
        }
    };

    const getStatusBg = (status: TicketStatus) => {
        switch (status) {
            case 'open': return 'rgba(34,197,94,0.12)';
            case 'in_progress': return 'rgba(59,130,246,0.12)';
            case 'forwarded': return 'rgba(139,92,246,0.12)';
            case 'resolved': return 'rgba(6,182,212,0.12)';
            case 'escalated': return 'rgba(239,68,68,0.12)';
            case 'overdue': return 'rgba(220,38,38,0.12)';
            case 'reopened': return 'rgba(245,158,11,0.12)';
            case 'rejected': return 'rgba(239,68,68,0.12)';
            default: return 'rgba(34,197,94,0.12)';
        }
    };

    const columns: CustomTableColumn<Ticket>[] = [
        {
            header: 'Ticket ID',
            key: 'id',
            minWidth: '95px',
            width: '10%',
            render: (t) => (
                <span style={{ fontWeight: 600, color: 'var(--accent-primary)', background: 'var(--bg-secondary)', padding: '0.25rem 0.55rem', borderRadius: '6px', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                    #{t.id ? t.id.slice(-6).toUpperCase() : 'N/A'}
                </span>
            )
        },
        {
            header: 'Subject',
            key: 'subject' as any,
            minWidth: '220px',
            width: '31%',
            render: (t) => (
                <div style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.2rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {t.subject || t.name}
                    </div>
                    {t.description && (
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <MessageSquare size={12} style={{ flexShrink: 0 }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.description}</span>
                        </div>
                    )}
                </div>
            )
        },
        {
            header: 'Department',
            key: 'department_id' as any,
            minWidth: '130px',
            width: '16%',
            render: (t) => (
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                    padding: '0.25rem 0.6rem', borderRadius: '6px',
                    fontSize: '0.78rem', fontWeight: 600,
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-secondary)',
                    borderLeft: `3px solid ${DEPT_COLOR_MAP[t.departments?.name || ''] ?? 'var(--accent-primary)'}`,
                    whiteSpace: 'nowrap'
                }}>
                    {t.departments?.name || 'General'}
                </span>
            )
        },
        {
            header: 'Student',
            key: 'student_id' as any,
            minWidth: '130px',
            width: '15%',
            render: (t) => (
                <span style={{ fontWeight: 500, color: 'var(--text-primary)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                    {t.students?.name || t.creator_details?.name || '—'}
                </span>
            )
        },
        {
            header: 'Status',
            key: 'status',
            minWidth: '120px',
            width: '15%',
            render: (t) => (
                <span style={{
                    fontSize: '0.75rem', fontWeight: 600, padding: '0.25rem 0.7rem', borderRadius: '20px',
                    display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                    background: getStatusBg(t.status),
                    color: getStatusColor(t.status),
                    textTransform: 'capitalize',
                    whiteSpace: 'nowrap'
                }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: getStatusColor(t.status), flexShrink: 0 }} />
                    {t.status ? t.status.replace(/_/g, ' ') : 'unassigned'}
                </span>
            )
        },
        {
            header: 'Created',
            key: 'created_at',
            minWidth: '120px',
            width: '16%',
            render: (t) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-muted)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    <Clock size={13} style={{ flexShrink: 0 }} />
                    <span>{new Date(t.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
            )
        }
    ];

    return (
        <div className="page-container" style={{ maxWidth: '1600px', margin: '0 auto' }}>
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Ticketing Portal', icon: <TicketIcon size={14} /> }
                ]}
            />

            <CustomCommonScreenHeader
                title="Support Tickets"
                subtitle="Manage school-wide queries, track status, and monitor department performance."
                icon={<TicketIcon size={24} />}
            />

            {/* Quick Status Filters */}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem', overflowX: 'auto', paddingBottom: '0.5rem', scrollbarWidth: 'none' }}>
                {TICKET_STATUS_OPTIONS.map((opt) => (
                    <button
                        key={opt.value}
                        onClick={() => { setStatusFilter(opt.value); setPage(1); }}
                        style={{
                            padding: '0.45rem 0.9rem',
                            borderRadius: '20px',
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            border: '1px solid',
                            borderColor: statusFilter === opt.value ? 'var(--accent-primary)' : 'var(--border-color)',
                            background: statusFilter === opt.value ? 'var(--accent-primary)' : 'var(--card-bg)',
                            color: statusFilter === opt.value ? '#ffffff' : 'var(--text-secondary)',
                            transition: 'all 0.2s ease',
                            boxShadow: statusFilter === opt.value ? '0 2px 8px rgba(99, 102, 241, 0.25)' : 'none'
                        }}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            {/* Action Bar (Search, Dept, Date range, Refresh) */}
            <div className="table-filter-bar" style={{ marginTop: '0.75rem' }}>
                <div className="table-filter-group" style={{ flex: '1 1 100%' }}>
                    {/* Search */}
                    <div className="table-search-box">
                        <Search size={16} className="table-search-icon" />
                        <input
                            type="text"
                            placeholder="Search by ID, subject, text..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {search && (
                            <button
                                className="table-search-clear"
                                onClick={() => setSearch('')}
                                title="Clear search"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    {/* Department Dropdown */}
                    {departments.length > 0 && (
                        <div className="table-select-box">
                            <CustomSearchableSelect
                                placeholder="Filter by Dept"
                                options={[
                                    { label: 'All Departments', value: 'all' },
                                    ...departments.map(d => ({ label: d.name, value: d.id }))
                                ]}
                                value={deptFilter}
                                onChange={(val) => {
                                    setDeptFilter(val as string);
                                    setPage(1);
                                }}
                                hideAvatar
                            />
                        </div>
                    )}

                    {/* Date range filter */}
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        background: 'var(--card-bg, #ffffff)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '0 0.6rem',
                        height: '38px',
                        boxSizing: 'border-box',
                        flexWrap: 'nowrap'
                    }}>
                        <Clock size={14} color="var(--text-muted)" style={{ flexShrink: 0 }} />
                        <input
                            type="date"
                            value={fromDate}
                            max={toDate || getTodayDateString()}
                            onChange={e => handleFromDateChange(e.target.value)}
                            title="From date"
                            style={{ border: 'none', background: 'transparent', color: 'var(--text-primary)', fontSize: '0.8rem', outline: 'none', cursor: 'pointer', padding: 0 }}
                        />
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>→</span>
                        <input
                            type="date"
                            value={toDate}
                            min={fromDate || undefined}
                            max={getTodayDateString()}
                            onChange={e => handleToDateChange(e.target.value)}
                            title="To date"
                            style={{ border: 'none', background: 'transparent', color: 'var(--text-primary)', fontSize: '0.8rem', outline: 'none', cursor: 'pointer', padding: 0 }}
                        />
                        {(fromDate || toDate) && (
                            <button
                                onClick={() => { setFromDate(''); setToDate(''); setPage(1); }}
                                title="Clear dates"
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', padding: '2px' }}
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    {/* Refresh Button */}
                    <CustomButton
                        variant="outline"
                        color="primary"
                        size="small"
                        onClick={fetchTickets}
                        disabled={loading}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <RotateCw size={14} className={loading ? 'animate-spin' : ''} />
                            <span>Refresh</span>
                        </div>
                    </CustomButton>

                    {/* Reset Filters Button */}
                    {(statusFilter !== 'all' || deptFilter !== 'all' || fromDate || toDate || search) && (
                        <CustomButton
                            variant="outline"
                            color="secondary"
                            size="small"
                            onClick={handleResetFilters}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <X size={14} />
                                <span>Reset</span>
                            </div>
                        </CustomButton>
                    )}
                </div>

                {error && (
                    <div style={{ color: 'var(--tkt-red)', fontSize: '0.85rem', fontWeight: 500, width: '100%' }}>
                        {error}
                    </div>
                )}
            </div>

            {/* Premium Custom Table with Unified Pagination */}
            <CustomTable
                columns={columns}
                data={filteredTickets}
                isCompact={true}
                isLoading={loading}
                minWidth="880px"
                emptyMessage="No tickets found"
                emptyIcon={<Inbox size={32} />}
                onRowClick={(t) => navigate(`/${schoolSlug}/tickets/${t.id}`)}
                pagination={{
                    currentPage: page,
                    totalPages: totalPages,
                    totalCount: totalCount,
                    pageSize: pageSize,
                    onPageChange: setPage,
                    hasPrevious: page > 1,
                    hasNext: page < totalPages,
                    isLoading: loading,
                    itemName: 'tickets',
                }}
            />
        </div>
    );
};
